<template>
    <div class="box-typical-upload box-typical-upload-in" style="width: 100%;">
         <div v-if="description" class="alert alert-info" role="alert">
             <img src="/img/icons/dragable-bl.svg" alt="Dragable"><span v-html="description"></span></div>
        <drop v-on:dragleave="onDragLeave"
            v-on:dragover="onDragOver"
            v-on:drop="onFileDropped">
            <div v-if="items.length <= 0" class="drop-zone-wrapper">
                <div :class="['drop-zone', isDragging ? 'dragover' : '']"
                    style="width: auto; height: 18em;">
                    <img src="/img/icons/cloud-bl.svg" alt="Cloud">
                    <div class="drop-zone-caption">
                        <h2>You haven’t assigned any {{ _.plural(assetType) }} to this content yet.</h2>
                        <p>Drag {{ _.plural(assetType) }} here to upload directly or select existing {{ _.plural(assetType) }} from your site assets</p>
                    </div>
                    <button v-on:click="openModal" type="button" class="btn btn-lg btn-rounded btn-success">Select {{ _.plural(assetType) }}</button> or
                    <span class="btn btn-lg btn-rounded btn-file btn-primary">
                        <span>Upload {{ _.plural(assetType) }}</span>
                        <input type="file"
                            v-on:change="onFileSelected"
                            multiple />
                    </span>
                </div>
            </div><!--.drop-zone-->
            <div v-else :class="['drop-zone-compact', 'more-images', isDragging ? 'dragover' : '']">
                <img src="/img/icons/cloud-bl.svg" alt="Cloud">
                <div class="drop-zone-caption">
                    <h2>Upload more {{ _.plural(assetType) }}</h2>
                    <p>Drag {{ _.plural(assetType) }} here to upload</p>
                </div>
                <div class="upload-buttons">
                    <button v-on:click="openModal" type="button" class="btn btn-lg btn-rounded btn-success">Select {{ _.plural(assetType) }}</button> or
                    <button class="btn btn-lg btn-rounded btn-file btn-primary">
                        <span>Upload {{ _.plural(assetType) }}</span>
                        <input type="file"
                            v-on:change="onFileSelected"
                            multiple />
                    </button>
                </div>
            </div>
        </drop>
        <div v-if="items.length <= 0" class="alert alert-success" role="success">
            <a href="https://www.72dpi.co.nz/blog/how-to-resize-images-using-canva.html" target="_blank"><img src="/img/icons/question-gr.svg" alt="Question"></a> Not sure how to resize your images? We have a <a href="https://www.72dpi.co.nz/blog/how-to-resize-images-using-canva.html" target="_blank">tutorial</a> for that.
        </div>
        <div v-if="files.length > 0 || rejectedFiles.length > 0" style="margin: 1em;">
            <h6 class="uploading-list-title">Uploading</h6>
            <ul class="uploading-list list-unstyled">
                <li v-for="file in files"
                    :key="file.name"
                    class="uploading-list-item">
                    <div class="uploading-list-item-wrapper">
                        <div class="uploading-list-item-name">
                            <i class="font-icon font-icon-cam-photo"></i>
                            {{file.name}}
                        </div>
                        <div class="uploading-list-item-size">{{getFileSize(file.size)}}</div>
                        <button v-on:click="removeFile(file.id)" type="button" class="uploading-list-item-close">
                            <i class="font-icon-close-2"></i>
                        </button>
                    </div>
                    <div class="progress">
                        <span class="progress-bar" :style="'width: ' + uploading[file.name] + '%;'"></span>
                    </div>
                    <div class="uploading-list-item-progress">{{uploading[file.name]}}%</div>
                    <div v-if="uploading[file.name] == 100" class="uploading-list-item-speed">Done</div>
                </li>
                <li v-for="file in rejectedFiles"
                    :key="file.name"
                    class="uploading-list-item" style="color: red;">
                    <div class="uploading-list-item-wrapper">
                        <div class="uploading-list-item-name">
                            <i class="font-icon font-icon-cam-photo"></i>
                            {{file.name}}
                        </div>
                        <div
                            class="uploading-list-item-size">Invalid extension</div>
                        <button v-on:click="removeFile(file.id)" type="button" class="uploading-list-item-close">
                            <i class="font-icon-close-2"></i>
                        </button>
                    </div>
                </li>
            </ul>
        </div>
        <file-modal-component
            v-on:change="pickValue"
            v-on:close="closeModal"
            :show="showModal"
            :closeModal="closeModal"
            :extensions="assetSchema.extensions"
            :storagePanel="storagePanel"
            :storages="storages"
			:multiple="true"
            ></file-modal-component>
    </div>
</template>
<script>
import Vue from 'vue';
import Vuex from 'vuex';
import filesize from 'filesize';
import mime from 'mime-types';

import { Drop } from 'vue-drag-drop';
import FileModalComponent from './FileModalComponent';

export default {
    data() {
        return {
            loading: false,
            isDragging: false,
            showModal: false,
			selected: null,
            files: [],
            rejectedFiles: [],
            fileList: [],
            uploading: {},
            error: {},
        }
    },

    props: ['type', 'items', 'schema', 'config', 'parent'],

    components: {
        FileModalComponent,
        Drop,
    },

    computed: {
        assetSchema() {
            return _.values(this.schema)
                .find(fieldSchema => {
                    if (this.assetField) {
                        return fieldSchema.name == this.assetField;
                    }
                    return fieldSchema.type == 'asset';
                });
        },

        assetField() {
            return _.get(this.config, 'assetField');
        },

        assetType() {
            return _.get(this.config, 'assetType', 'file');
        },

        storagePanel() {
            return _.get(this.config, 'storagePanel', null);
        },

        storages() {
            return _.get(this.config, 'storages', null);
        },

        description() {
            let description = _.get(this.config, 'description');

            if (_.isFunction(description)) {
                description = description(this.items, this.parent);
            }

            return description;
        }
    },

    methods: {
        ...Vuex.mapActions('assets', {
            createData: 'createData',
        }),

        onFileDropped(_, e) {
            e.preventDefault();
            this.isDragging = false;
            const files = this.getFileArray(e.dataTransfer.files);
            this.files = this.files.concat(files);
            this.uploadFiles(files);
        },

        onFileSelected(e) {
            e.preventDefault();
            const files = this.getFileArray(e.target.files);
            this.files = this.files.concat(files);
            this.uploadFiles(files);
        },

        onDragOver(_, e) {
            e.preventDefault();
            this.isDragging = true;
        },

        onDragLeave(_, e) {
            e.preventDefault();
            this.isDragging = false;
        },

        getFileArray(fileList) {
            const length = fileList.length;
            const fileArray = [];
            const timestamp = Date.now();
            for (let i = 0; i < length; i++) {
                const file = fileList.item(i);
                file.id = `${timestamp}.${i}`;
                fileArray.push(fileList.item(i));
            }
            return fileArray;
        },

        getFileSize(val) {
            return filesize(val);
        },

        removeFile(id) {
            this.files = this.files.filter(file => file.id != id);
            this.rejectedFiles = this.rejectedFiles.filter(file => file.id != id);
        },

        pickValue(value) {
            if (value.id == '_assets') {
                this.$emit('input::batch', value.data.map((asset) => {
                    return { [this.assetSchema.name]: asset.id }
                }))
                return;
            }
            this.$emit('input', {
                [this.assetSchema.name]: value.id
            });
        },

        uploadFiles(files) {
            this.files = files.filter(file => this.assetSchema.extensions.indexOf(mime.extension(file.type)) >= 0);
            this.rejectedFiles = files.filter(file => this.assetSchema.extensions.indexOf(mime.extension(file.type)) < 0);

            Promise.all(this.files.map(file => {
                this.uploading[file.name] = 0;
                return this.createAsset(file)
                    .then((response) => {
                        this.$emit('input', {
                            [this.assetSchema.name]: response.id,
                        });
                        this.uploading = { ...this.uploading, [file.name]: 100 };
                    })
            }))
        },

        createAsset(file) {
            return this.createData({
                data: { file },
                options: { upload: true },
            });
        },

        openModal() {
            this.showModal = true;
        },

        closeModal() {
            this.showModal = false;
        }
    }

}
</script>



// WEBPACK FOOTER //
// seventytwo/72cms-ui/src/components/helpers/InlineUploadComponent.vue